import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { QUERY_GET_SLUG_RELEASE_PRODUCT } from '~/components/SneakerRelease/graphql';
import { transformReleaseProduct } from '~/services/sneaker-release/sneaker-release-transform';
import sneakerReleaseAtom from '~/services/sneaker-release/sneaker-release.atom';
import { ReleaseType, SneakLaunch, SneakLaunchResponse } from '~/services/sneaker-release/sneaker-release.type';
import InfiniteScroll from 'react-infinite-scroll-component';
import ProductLaunch from '~/components/SneakerRelease/Product';
import { release } from 'os';

type Props = {
    brandCode?: string;
};

const Title = ({ month }: { month: string }) => {
    return (
        <header className="flex items-center justify-between">
            <p className="text-[2rem] tracking-[-0.03em] leading-[1.2]">{month}</p>
            <p className="text-[2rem] tracking-[-0.03em] leading-[1.2]">2024</p>
        </header>
    );
};

const SneakerProductList = ({ brandCode }: Props) => {
    const { releaseType } = useRecoilValue(sneakerReleaseAtom.sneakerRelease);
    const [page, setPage] = useState(1);

    const {
        data: fetchReleaseProduct,
        loading,
        fetchMore,
        networkStatus
    } = useQuery<SneakLaunchResponse>(QUERY_GET_SLUG_RELEASE_PRODUCT, {
        variables: {
            page: 1,
            pageSize: 10,
            brand: brandCode
        },
        notifyOnNetworkStatusChange: true
    });
    const { data: fetchReleaseProductNextMonth } = useQuery<SneakLaunchResponse>(QUERY_GET_SLUG_RELEASE_PRODUCT, {
        variables: {
            page: 1,
            pageSize: 4,
            fromDate: dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD'),
            toDate: dayjs().add(1, 'month').endOf('month').format('YYYY-MM-DD'),
            brand: brandCode,
            releaseDate: 'DESC'
        },
        notifyOnNetworkStatusChange: true
    });
    const { items = [], totalCount, totalPages = 1 } = fetchReleaseProduct?.getReleaseProducts || {};
    const productNextMonth = fetchReleaseProductNextMonth?.getReleaseProducts?.items?.map((i) =>
        transformReleaseProduct(i)
    );
    const loadMore = () => {
        if (loading) return;
        setPage(page + 1);
        fetchMore({
            variables: {
                page: page + 1,
                pageSize: 12
            },
            updateQuery: (previousData, { fetchMoreResult }): any => {
                if (!fetchMoreResult) return previousData;
                const prevData = previousData['getReleaseProducts']?.items ?? [];
                const moreData = fetchMoreResult['getReleaseProducts']?.items ?? [];
                fetchMoreResult['getReleaseProducts'].items = [...prevData, ...moreData];
                return fetchMoreResult;
            }
        });
    };
    const products = useMemo(() => {
        const loadingItems = Array.from({ length: 16 }).map((_, index) => ({ id: index })) as SneakLaunch[];
        const newItems = items?.map((i) => transformReleaseProduct(i)) || [];
        if ([3, 7].includes(networkStatus)) {
            return newItems;
        } else {
            newItems.concat(loadingItems);
        }
    }, [items, networkStatus]);
    return (
        <>
            <section className="px-2 md:px-8 flex flex-col gap-10">
                {!isEmpty(productNextMonth) && (
                    <>
                        <div className="mb-10">
                            <div className="mb-2">
                                <Title month={dayjs().add(1, 'month').format('MMMM')} />
                            </div>
                            <ProductLaunch products={productNextMonth} loading={loading} />
                        </div>
                    </>
                )}
                <InfiniteScroll
                    dataLength={items.length}
                    next={loadMore}
                    hasMore={totalPages > page}
                    loader={<div>loading...</div>}
                    scrollThreshold={0.9}
                >
                    <ProductLaunch products={products} loading={loading && networkStatus !== 3} />
                </InfiniteScroll>
            </section>
        </>
    );
};

export default SneakerProductList;
